import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { PhotoStreamClient } from '../api/PhotoStreamClient';

interface CameraUploadProps {
  galleryId: string;
  onSuccess?: () => void;
}

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const CameraUpload: React.FC<CameraUploadProps> = ({ galleryId, onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadCount, setUploadCount] = useState(0);
  const {
    uploading,
    progress,
    error,
    success,
    moderationStatus,
    moderationMessage,
    isAwaitingModeration,
    uploadPhoto,
    reset
  } = usePhotoUpload(apiClient);

  useEffect(() => {
    if (success) {
      setUploadCount(prev => prev + 1);
      onSuccess?.();
    }
  }, [success, onSuccess]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL with cleanup
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      reset();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    await uploadPhoto(galleryId, selectedFile);
  };

  const handleTakePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('capture', 'environment');
      fileInputRef.current.click();
    }
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {!selectedFile && !success && (
        <Card>
          <CardHeader>
            <CardTitle>Take a Photo</CardTitle>
            <CardDescription>
              Use your camera to capture and upload photos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={handleTakePhoto} className="w-full" size="lg">
              📷 Take Photo
            </Button>
            <Button onClick={handleChooseFile} variant="outline" className="w-full">
              📁 Choose from Gallery
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedFile && !success && (
        <Card>
          <CardHeader>
            <CardTitle>Photo Selected</CardTitle>
            <CardDescription>
              {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="w-full max-w-sm mx-auto">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Selected preview"
                  className="w-full h-auto rounded-lg border"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                  onError={() => {
                    console.warn('Image preview failed to load');
                    setPreviewUrl(null);
                  }}
                />
              ) : (
                <div className="w-full h-48 bg-muted rounded-lg border flex items-center justify-center">
                  <span className="text-muted-foreground">Preview not available</span>
                </div>
              )}
            </div>
            
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {(uploading || isAwaitingModeration) && (
              <div className="space-y-2">
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  {isAwaitingModeration ? 'Awaiting moderation...' : `Uploading... ${progress}%`}
                </p>
              </div>
            )}

            {moderationMessage && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
                {moderationMessage}
              </div>
            )}

            <div className="flex space-x-2">
              <Button 
                onClick={handleUpload} 
                disabled={uploading}
                className="flex-1"
              >
                {uploading ? (isAwaitingModeration ? 'Awaiting moderation...' : 'Uploading...') : 'Upload Photo'}
              </Button>
              <Button 
                onClick={handleReset} 
                variant="outline"
                disabled={uploading}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="text-4xl">✅</div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">
                  Photo Uploaded Successfully!
                </h3>
                <p className="text-sm text-green-600">
                  {uploadCount === 1 
                    ? "Your photo has been added to the gallery." 
                    : `${uploadCount} photos uploaded to the gallery.`
                  }
                </p>
              </div>
              <div className="flex space-x-2 justify-center">
                <Button onClick={handleReset} variant="outline">
                  Upload Another Photo
                </Button>
                <Button onClick={onSuccess}>
                  View Gallery
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
